import { cn } from '@/app/modules/shared/lib/utils'
import { ComponentProps, ElementType, HTMLAttributes } from 'react'

export type PolymorphicProps<E extends ElementType> = {
  as?: E
} & Omit<ComponentProps<E>, 'as'>

// Design system: mobile defaults, md: = desktop. Sizes in rem (1rem = 16px root).
const typographyVariants = {
  h1: {
    className:
      'font-gt-planar font-normal text-[1.75rem] leading-[2rem] tracking-[-0.035rem] md:text-[2.5rem] md:leading-[2.75rem] md:tracking-[-0.05rem]',
    defaultElement: 'h1' as const,
  },
  h2: {
    className:
      'font-gt-planar font-normal text-[1.5rem] leading-[1.75rem] tracking-[-0.03rem] md:text-[2rem] md:leading-[2.25rem] md:tracking-[-0.035rem]',
    defaultElement: 'h2' as const,
  },
  h3: {
    className:
      'font-gt-planar font-normal text-[1.375rem] leading-[1.5rem] tracking-[-0.0275rem] md:text-[1.625rem] md:leading-[1.875rem] md:tracking-[-0.0325rem]',
    defaultElement: 'h3' as const,
  },
  h4: {
    className:
      'font-sans font-extrabold text-[1.125rem] leading-[1.875rem] tracking-[-0.0225rem] md:text-[1.375rem] md:tracking-[-0.0275rem]',
    defaultElement: 'h4' as const,
  },
  h5: {
    className:
      'font-gt-planar font-normal text-[1rem] leading-[1.25rem] md:text-[1.125rem] md:leading-[1.5rem]',
    defaultElement: 'h5' as const,
  },
  subtitle: {
    className:
      'font-gt-planar font-normal text-[0.875rem] leading-[1.25rem] md:text-[1rem] md:leading-[1.375rem]',
    defaultElement: 'p' as const,
  },
  body1: {
    className: 'font-sans font-normal text-[1rem] leading-[1.5rem]',
    defaultElement: 'p' as const,
  },
  body2: {
    className:
      'font-sans font-normal text-[0.875rem] leading-[1.25rem] tracking-[-0.015rem]',
    defaultElement: 'p' as const,
  },
  caption: {
    className: 'font-sans font-normal text-[0.75rem] leading-[1rem]',
    defaultElement: 'p' as const,
  },
} as const

export type TypographyVariant = keyof typeof typographyVariants

export type TypographyProps<E extends ElementType = 'p'> = {
  variant?: TypographyVariant
  as?: E
  className?: string
} & Omit<ComponentProps<E>, 'as' | 'className'>

export function Typography<E extends ElementType = 'p'>({
  variant = 'body1',
  as,
  className,
  children,
  ...props
}: TypographyProps<E>) {
  const variantConfig = typographyVariants[variant]
  const Component = (as || variantConfig.defaultElement) as ElementType

  return (
    <Component
      className={cn(
        'Typography',
        `Typography--${variant}`,
        variantConfig.className,
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

/**
 * Section heading used across the onboarding wizard steps. Mirrors the
 * marketplace `Headline2` exactly (GT Planar — `font-gt-planar` here is the
 * same family the marketplace calls `font-title`).
 */
export function Headline2({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn(
        'font-gt-planar text-[1.25rem] tracking-[-0.025rem] leading-[120%] md:text-[1.875rem] md:tracking-normal',
        className
      )}
      {...props}
    >
      {children}
    </h2>
  )
}


